import logger from "@logger";
import { useEffect, useState } from "react";

const COARSE_POINTER_QUERY = "(pointer: coarse)";
const NARROW_WIDTH_QUERY = "(max-width: 1024px)";

function matchesMedia(query: string): boolean {
  if (typeof globalThis.matchMedia !== "function") {
    return false;
  }
  return globalThis.matchMedia(query).matches;
}

/**
 * Whether the device looks like a phone/tablet: a coarse pointer on a narrow
 * viewport. Width is unaffected by the on-screen keyboard, so this stays
 * stable while the user is typing.
 */
function isMobileViewport(): boolean {
  return matchesMedia(COARSE_POINTER_QUERY) && matchesMedia(NARROW_WIDTH_QUERY);
}

/**
 * True device orientation. Uses the Screen Orientation API so the on-screen
 * keyboard — which shrinks the viewport height and can make a CSS
 * `orientation: landscape` query match while the device is upright — does not
 * produce a false positive. Falls back to legacy `window.orientation` and
 * finally a media query when the API is unavailable.
 */
function isLandscapeOrientation(): boolean {
  const orientationType = globalThis.screen?.orientation?.type;
  if (typeof orientationType === "string") {
    return orientationType.startsWith("landscape");
  }

  const legacyOrientation = (globalThis as { orientation?: number }).orientation;
  if (typeof legacyOrientation === "number") {
    return Math.abs(legacyOrientation) === 90;
  }

  return matchesMedia("(orientation: landscape)");
}

function shouldBlock(): boolean {
  return isMobileViewport() && isLandscapeOrientation();
}

function tryLockPortrait() {
  try {
    const orientation = globalThis.screen?.orientation as
      | (ScreenOrientation & { lock?: (orientation: string) => Promise<void> })
      | undefined;
    if (orientation && typeof orientation.lock === "function") {
      orientation.lock("portrait").catch(() => {});
    }
  } catch {
    logger.info("screen.orientation.lock not supported");
  }
}

/**
 * Full-screen gate that blocks interaction when a mobile device is held in
 * landscape. Orientation is read from the Screen Orientation API rather than
 * the viewport aspect ratio, so opening the keyboard in portrait no longer
 * triggers the gate. Also attempts `screen.orientation.lock("portrait")` as a
 * best-effort lock (works on Android Chrome; no-op on iOS Safari).
 */
export function PortraitGate() {
  const [isBlocked, setIsBlocked] = useState(shouldBlock);

  useEffect(() => {
    tryLockPortrait();
  }, []);

  useEffect(() => {
    const update = () => setIsBlocked(shouldBlock());
    update();

    const orientation = globalThis.screen?.orientation;
    orientation?.addEventListener?.("change", update);
    globalThis.addEventListener?.("orientationchange", update);
    globalThis.addEventListener?.("resize", update);

    return () => {
      orientation?.removeEventListener?.("change", update);
      globalThis.removeEventListener?.("orientationchange", update);
      globalThis.removeEventListener?.("resize", update);
    };
  }, []);

  if (!isBlocked) {
    return null;
  }

  return (
    <div
      data-testid="portrait-gate"
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white"
    >
      <div className="animate-bounce text-5xl mb-6">📱</div>
      <p className="text-lg font-semibold text-gray-800 text-center px-8">Please rotate your device to portrait</p>
      <p className="text-sm text-gray-500 mt-2 text-center px-8">Fitties is designed for portrait mode</p>
    </div>
  );
}

export default PortraitGate;
